/**
 * Summary statistics for the respiration report table.
 *
 * The report shows a table instead of the time-series chart
 * (project_management#540): on daytime recordings the display gates withhold
 * most points and the chart reads as broken, while the surviving windows still
 * support solid summary statistics.
 *
 * Two window populations are summarised side by side (project_management#549):
 *
 * - "All measured": windows with confidence >= 0.3 — the same population the
 *   analysis service summarises, so its median matches the summary table.
 * - "Within validated range": the above, further limited to rate <= 18 br/min.
 *
 * Neither is a "resting" rate: nothing here measures activity, and the only
 * difference between the two is the rate cap. Showing one without the other
 * misled — the capped median drops the upper half of a daytime distribution,
 * while the uncapped one includes readings above the validated range, where
 * the estimator can underestimate fast breathing. Each is presented with its
 * own coverage so the reader can see how much of the recording it rests on.
 *
 * The all-measured median and coverage are taken from the analysis-service
 * summary when it is available: it computes the median over unrounded rates,
 * while the stored series is rounded to 0.1, so recomputing here can land 0.1
 * off the summary-table row (17.95 -> 18.0 vs 17.9 on the #549 sequence) — the
 * very inconsistency this table exists to remove.
 */
import {
  RESPIRATION_MIN_CONFIDENCE,
  RESPIRATION_MAX_DISPLAY_BRPM,
} from './respirationDisplay';
import { RespirationMetricsData, RespirationTimeSeriesData } from '../types/respirationTimeSeries';

export const NIGHT_START_HOUR = 23;
export const NIGHT_END_HOUR = 7;

/**
 * A night median from a handful of windows would be noise presented as sleep
 * data; require at least half an hour of resolved night windows before showing it.
 */
export const MIN_NIGHT_WINDOWS = 30;

export interface RespirationPopulationSummary {
  /** Median rate over the population's windows, br/min. */
  medianBrpm: number;
  /** 5th and 95th percentile, br/min. */
  rangeBrpm: { low: number; high: number };
  /** Minutes that produced a measurement (one window = one minute). */
  minutes: number;
  /** Measured minutes as a share of the recorded span, 0-100. */
  coveragePercent: number;
}

export interface RespirationReportSummary {
  /** Every window that met the confidence floor. */
  allMeasured: RespirationPopulationSummary;
  /** Confident windows at or below the validated ceiling; null if none. */
  validatedRange: RespirationPopulationSummary | null;
  /** Median over validated-range windows falling in 23:00-07:00 local; null
   * if the sequence has fewer than MIN_NIGHT_WINDOWS such windows. */
  nightMedianBrpm: number | null;
}

function percentile(sorted: number[], p: number): number {
  const idx = (sorted.length - 1) * p;
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  const weight = idx - lo;

  return sorted[lo] * (1 - weight) + sorted[hi] * weight;
}

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

function summarise(
  rates: number[],
  totalWindows: number,
  windowMinutes: number,
): RespirationPopulationSummary | null {
  if (!rates.length) {
    return null;
  }

  const sorted = [...rates].sort((a, b) => a - b);

  return {
    medianBrpm: round1(percentile(sorted, 0.5)),
    rangeBrpm: {
      low: round1(percentile(sorted, 0.05)),
      high: round1(percentile(sorted, 0.95)),
    },
    minutes: Math.round(rates.length * windowMinutes),
    coveragePercent: Math.round((rates.length / totalWindows) * 100),
  };
}

/**
 * Compute the report summary, or null when no window met the confidence floor
 * (the caller hides the section, matching the no-phantom-card behaviour of the
 * chart it replaces).
 *
 * `startTimeMs` is the sequence start as a millisecond timestamp; it anchors
 * window offsets to local wall-clock time for the night split. `serverSummary`
 * is the analysis-service summary behind the report's summary-table row.
 */
export function computeRespirationSummary(
  data: RespirationTimeSeriesData | null | undefined,
  startTimeMs: number,
  serverSummary?: RespirationMetricsData | null,
): RespirationReportSummary | null {
  if (!data?.timestamps?.length) {
    return null;
  }

  const rates = data.respirationRate ?? [];
  const confidences = data.confidence ?? [];
  const measuredRates: number[] = [];
  const validatedRates: number[] = [];
  const nightRates: number[] = [];

  for (let i = 0; i < data.timestamps.length; i += 1) {
    const rate = rates[i];
    const confidence = confidences[i];

    const measured = typeof rate === 'number'
      && Number.isFinite(rate)
      && typeof confidence === 'number'
      && confidence >= RESPIRATION_MIN_CONFIDENCE;

    if (measured) {
      measuredRates.push(rate);

      if (rate <= RESPIRATION_MAX_DISPLAY_BRPM) {
        validatedRates.push(rate);

        const hour = new Date(startTimeMs + data.timestamps[i] * 1000).getHours();

        if (hour >= NIGHT_START_HOUR || hour < NIGHT_END_HOUR) {
          nightRates.push(rate);
        }
      }
    }
  }

  const totalWindows = data.timestamps.length;
  const windowMinutes = (data.windowDurationSec ?? 60) / 60;
  const computed = summarise(measuredRates, totalWindows, windowMinutes);

  if (!computed) {
    return null;
  }

  const allMeasured: RespirationPopulationSummary = {
    ...computed,
    medianBrpm: serverSummary?.medianRrBrpm ?? computed.medianBrpm,
    coveragePercent:
      serverSummary?.coveragePercent != null
        ? Math.round(serverSummary.coveragePercent)
        : computed.coveragePercent,
  };

  return {
    allMeasured,
    validatedRange: summarise(validatedRates, totalWindows, windowMinutes),
    nightMedianBrpm:
      nightRates.length >= MIN_NIGHT_WINDOWS
        ? round1(percentile([...nightRates].sort((a, b) => a - b), 0.5))
        : null,
  };
}

/**
 * The summary-table pair for respiration: the validated-range median and its
 * own coverage (project_management#549, agreed with Žilvinas 2026-09-26). The
 * same population as the "Within validated range" block of the report
 * section, so the two can never disagree. Null when no confident window fell
 * inside the range — the table then shows no respiration rows rather than a
 * rate without a population.
 */
export function respirationForSummaryTable(
  report: RespirationReportSummary | null,
): { medianRrBrpm: number; coveragePercent: number } | null {
  const population = report?.validatedRange;

  return population
    ? { medianRrBrpm: population.medianBrpm, coveragePercent: population.coveragePercent }
    : null;
}

/** Format whole minutes as "7 h 08 min" / "45 min" for the table. */
export function formatMeasuredDuration(totalMinutes: number): string {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours === 0) {
    return `${minutes} min`;
  }

  return `${hours} h ${String(minutes).padStart(2, '0')} min`;
}
